import useKPIPendingCreations from '@hooks/useKPIPendingCreations';
import { KPIType, KPIVariant, RootKPI } from '@services/api/deckManagement/graphql/schema/API';
import { useGetRootKPIs, useGetVariantsForRootKPI } from '@services/api/deckManagement/hooks';
import { isEqual } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KPILibraryContextType, KPILibraryProviderProps } from 'types/KPILibraryContext.types';

/**
 * KPI library Context
 *
 * Used to manage the state of the KPI library
 */
export const KPILibraryContext = createContext<KPILibraryContextType>({
  rootKPIs: [],
  pendingCreations: {
    allRootKPIs: [],
    pendingRootKPIs: [],
    pendingVariants: [],
    addPendingRootKPI: () => '',
    addPendingVariant: () => ({
      __typename: 'KPIVariant' as const,
      pk: '',
      sk: '',
      kpi_variant_id: '',
      variant_name: '',
      variant_overrides: null,
      kpi_type: KPIType.BASE,
      root_kpi_id: '',
      isNew: true,
    }),
    updatePendingVariant: () => {},
    getEnhancedVariants: () => [],
    hasPendingCreations: false,
    resolvePendingCreations: () => Promise.resolve({ success: true }),
    clearPendingCreations: () => {},
  },
  selectedRootKPI: null,
  setSelectedRootKPI: () => {},
  variants: [],
  selectedVariant: null,
  setSelectedVariant: () => {},
  isLoading: false,
  isError: false,
  error: null,
});

export const KPILibraryProvider = ({ children }: KPILibraryProviderProps): ReactNode => {
  // Fetch existing list of Root KPIs available
  const {
    data: currRootKPIs,
    isLoading: isRootKPIsLoading,
    isError: isRootKPIsError,
    error: rootKPIError,
  } = useGetRootKPIs({ variantFields: ['kpi_variant_id', 'dataset_source'] });

  // Derive Root KPIs directly from query result
  const rootKPIs = useMemo(() => currRootKPIs ?? [], [currRootKPIs]);

  // Pending creation management
  const pendingCreations = useKPIPendingCreations(rootKPIs);

  // Selected Root KPI state
  const [selectedRootKPI, setSelectedRootKPIState] = useState<RootKPI | null>(null);

  // Selected Variant state
  const [selectedVariant, setSelectedVariantState] = useState<KPIVariant | null>(null);
  const setSelectedVariant = useCallback((variant: KPIVariant | null) => {
    setSelectedVariantState(variant);
  }, []);

  const setSelectedRootKPI = useCallback((rootKPI: RootKPI | null) => {
    setSelectedRootKPIState(rootKPI);
    setSelectedVariantState(null);
  }, []);

  // Fetch variants for the selected Root KPI (enabled only when a Root KPI is selected)
  const {
    data: currVariants,
    isLoading: isVariantsLoading,
    isError: isVariantsError,
    error: variantsError,
  } = useGetVariantsForRootKPI({
    root_kpi_id: selectedRootKPI?.root_kpi_id ?? '',
    options: { enabled: !!selectedRootKPI },
  });

  const variants = useMemo(() => currVariants ?? [], [currVariants]);

  // Refs for accessing current selections without adding them as effect dependencies
  const selectedRootKPIRef = useRef(selectedRootKPI);
  const selectedVariantRef = useRef(selectedVariant);
  // Ensure they are updated between renders
  selectedRootKPIRef.current = selectedRootKPI;
  selectedVariantRef.current = selectedVariant;

  // Sync selectedRootKPI with refetched data (e.g. after any Subscription-triggered invalidation)
  useEffect(() => {
    const { current } = selectedRootKPIRef;
    if (current && currRootKPIs) {
      const updatedRootKPI = currRootKPIs.find((rootKPI) => rootKPI.root_kpi_id === current.root_kpi_id);
      if (updatedRootKPI && !isEqual(updatedRootKPI, current)) {
        setSelectedRootKPIState(updatedRootKPI);
      } else if (!updatedRootKPI) {
        setSelectedRootKPIState(null);
        setSelectedVariantState(null);
      }
    }
  }, [currRootKPIs]);

  // Sync selectedVariant with refetched data (e.g. after any Subscription-triggered invalidation)
  useEffect(() => {
    const { current } = selectedVariantRef;
    if (current && currVariants) {
      const updatedVariant = currVariants.find((variant) => variant.kpi_variant_id === current.kpi_variant_id);
      if (updatedVariant && !isEqual(updatedVariant, current)) {
        setSelectedVariantState(updatedVariant);
      } else if (!updatedVariant) {
        setSelectedVariantState(null);
      }
    }
  }, [currVariants]);

  /**
   * Error handling for fetching Root KPIs
   *
   * Logs the error to the console if the query fails
   */
  useEffect(() => {
    if (rootKPIError) {
      console.error('Failed to fetch Root KPIs', rootKPIError);
    }
  }, [rootKPIError]);

  // Combined loading states
  const isLoading = isRootKPIsLoading || isVariantsLoading;

  // Combined error states
  const isError = isRootKPIsError || isVariantsError;
  const error = rootKPIError || variantsError;

  /**
   * Creates the KPI Library context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context.
   */
  const contextValue = useMemo<KPILibraryContextType>(
    () => ({
      rootKPIs,
      pendingCreations,
      selectedRootKPI,
      setSelectedRootKPI,
      variants,
      selectedVariant,
      setSelectedVariant,
      isLoading,
      isError,
      error,
    }),
    [
      rootKPIs,
      pendingCreations,
      selectedRootKPI,
      setSelectedRootKPI,
      variants,
      selectedVariant,
      setSelectedVariant,
      isLoading,
      isError,
      error,
    ],
  );

  return <KPILibraryContext value={contextValue}>{children}</KPILibraryContext>;
};

/**
 * Custom hook to access the KPILibrary context
 *
 * @returns {KPILibraryContextType} The KPI Library context value
 */
export const useKPILibraryContext = (): KPILibraryContextType => {
  return use(KPILibraryContext);
};
